import { clearQuerySession, queryClient } from "@/api/query-client";
import config from "@Config";
import initialState from "@Context/common/general/initial-state";
import firebaseConfig from "@FirebaseConfig";
import { ServiceOrigin } from "@Runtime/runtime-enums";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import amplitude from "amplitude-js"; //todo: check if this is an issue
import { initializeAnalytics } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import {
  connectAuthEmulator,
  getAuth,
  GoogleAuthProvider,
  indexedDBLocalPersistence,
  initializeAuth,
  OAuthProvider,
  onAuthStateChanged,
} from "firebase/auth";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { flatten } from "lodash-es";
import { merge } from "merge-anything";
import * as React from "react";
import { ApiLoadingProvider } from "./api-loading-context";
import { ApiSessionContext } from "./api-session-context";
import { GlobalState } from "./initial-state.types";

/**
 * Handlers
 */
import unityBridge from "../../handlers/unity-bridge";

import { ActionConfig, CoreActions } from "@Actions/action";

/**
 * Actions
 */
import * as AppActions from "@Actions/app";
import * as AuthActions from "@Actions/auth";
import * as ChallengesActions from "@Actions/challenges";
import * as ClubsActions from "@Actions/clubs";
import * as FirebaseActions from "@Actions/firebase";
import * as MetricActions from "@Actions/metrics";
import * as PaymentActions from "@Actions/payment";
import * as PlayersActions from "@Actions/players";
import * as ProgressionActions from "@Actions/progression";
import * as ReplayActions from "@Actions/replays";
import * as RewardsActions from "@Actions/rewards";
import * as ScenariosActions from "@Actions/scenarios";
import * as SecureActions from "@Actions/secure";
import * as SessionActions from "@Actions/sessions";
import * as StatsActions from "@Actions/stats";
import * as SubscriptionsActions from "@Actions/subscriptions";
import * as TeamsActions from "@Actions/teams";
import * as UsersActions from "@Actions/users";

const emulatorHost = process.config.EMULATOR_HOST || "localhost";
const unityLogs = !!process.config.UNITY_LOGS;
const isLocalFunctions = process.config.FUNCTIONS_ORIGIN === ServiceOrigin.Emulator;

const Actions = {
  CoreActions: {
    UpdateState: CoreActions.UpdateState,
    Reset: CoreActions.Reset,
    CloseError: CoreActions.CloseError,
    NoOp: CoreActions.NoOp,
  },
  AppActions,
  MetricActions,
  FirebaseActions,
  UsersActions,
  ClubsActions,
  PlayersActions,
  TeamsActions,
  SubscriptionsActions,
  ScenariosActions,
  SecureActions,
  ProgressionActions,
  AuthActions,
  PaymentActions,
  StatsActions,
  ReplayActions,
  SessionActions,
  ChallengesActions,
  RewardsActions,
} as const;
// Flat map actions for easier dispatch matching
const ActionMap = flatten(Object.values(Actions).map(actions => Object.values(actions)));

/**
 * Initialize firebase libraries
 */
const firebaseApp = initializeApp(config);

const firebaseAuth = initializeAuth(firebaseApp, {
  persistence: indexedDBLocalPersistence,
});
const firebaseAnalyics = initializeAnalytics(firebaseApp);

const oAuthProvider = new OAuthProvider("apple.com");
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: "select_account",
});

/**
 * Initialize Amplitude
 */
amplitude.getInstance().init(config.amplitude_api_key, undefined, {
  disableCookies: true,
  optOut: true,
  saveEvents: false,
  includeReferrer: false,
  includeUtm: false,
});

if (isLocalFunctions) {
  connectFunctionsEmulator(getFunctions(), emulatorHost, 5001);

  if (process.config.AUTH_ORIGIN === ServiceOrigin.Emulator) {
    connectAuthEmulator(getAuth(), `http://${emulatorHost}:${firebaseConfig.emulators.auth.port}`);
  }
}

type ValueOf<T> = T[keyof T];

/**
 * Setup Global Game Context
 */
let AppContext = React.createContext({
  state: initialState,
  dispatch: async (config: ActionConfig) => {},
  // dispatch with generic type casting to action config payload
  dispatchType: async <T extends ValueOf<ActionConfig>>(config: ActionConfig<T>) => {},
});

/**
 * To prevent React reducer triggering dispatched actions in duplicate on re-renders
 * we need to wrap the dispatch with a middleware to handle async actions independently
 */
function dispatchMiddleware(state: typeof initialState, dispatch: (config: ActionConfig) => void) {
  const dispatchAction = (action: ActionConfig) => {
    return new Promise((resolve, reject) => {
      if (unityLogs) {
        console.log("Action Intercept:", action);
      }

      // Find matching action type that isn't a core reducer action
      let MatchedAction = ActionMap.find((asyncAction: any) => typeof action.type !== "string" && asyncAction === action.type);

      const proxySuccess = (args: any) => {
        if (action.onSuccess) action.onSuccess(args);
        return resolve(args);
      };

      const proxyError = (args: any) => {
        if (action.onError) action.onError(args);
        return resolve(args);
      };

      if (MatchedAction) {
        // Instanciate matched action with common props
        new MatchedAction(
          firebaseApp,
          firebaseAuth,
          isLocalFunctions ? getFunctions(firebaseApp, "us-central1") : getFunctions(firebaseApp, config.region),
          currentState,
          firebaseAnalyics,
          amplitude.getInstance(),
          Sentry,
          unityBridge,
          dispatchAction,
          action.payload || {},
          proxySuccess,
          action.onError ? proxyError : undefined,
          action.disableLoaders || (action.payload ? action.payload.disableLoaders : false),
          action.cacheTime || (action.payload ? action.payload.cacheTime || action.payload.cache_time : null)
        );
      } else {
        // If no match we default to normal dispatch reducer for sync tasks
        dispatch(action);
        return resolve(null);
      }
    });
  };
  return dispatchAction;
}

/**
 * Action To State Reducer - Use for immediate state manipulation after async work is done.
 * @param {*} state
 * @param {*} action
 */
function reducer(state: GlobalState, action: ActionConfig) {
  if (unityLogs) {
    console.log("Action:", action.type, "Payload:", action.payload);
  }

  Sentry.addBreadcrumb({
    category: "AppContext",
    type: action.type,
    message: "Dispatched Action",
    data: action.payload,
  });

  switch (action.type) {
    case CoreActions.Reset:
      state = {
        ...merge(state, {
          user: {
            isAppLinkedToUser: false,
            isEmailVerified: false,
            accountLinkedUuIds: [],
          },
        }),
      };

      break;
    case CoreActions.CloseError:
      state = { ...state, error: { ...state.error, active: false } };
      break;
    case CoreActions.UpdateState:
      if (!action.payload) {
        console.warn("No payload provided for update state action");
        return state;
      }
      //@ts-ignore
      state = { ...merge(state, action.payload) };
      if (action.onSuccess) action.onSuccess(state);
      break;
    default:
      console.warn("No Actions matched in either context middleware or reducer", action);
      break;
  }
  unityBridge.updateState(state);
  return state;
}

// Workaround for useEffect closures referencing an old global state.
// This forces state to be hoisted outside the scope of the a useEffect invokation and then passed into dispatched actions
let currentState = initialState;

/**
 * Setup Provider Of State & Global Action Dispatcher
 */
const ContextProvider = (props: any) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const [firebaseUid, setFirebaseUid] = React.useState<string | null>(firebaseAuth.currentUser?.uid ?? null);
  React.useEffect(() => {
    let uid = firebaseAuth.currentUser?.uid;
    return onAuthStateChanged(firebaseAuth, user => {
      if (uid !== user?.uid) {
        clearQuerySession();
        uid = user?.uid;
      }
      setFirebaseUid(user?.uid ?? null);
    });
  }, []);
  currentState = state;
  const value = { state, dispatch: dispatchMiddleware(state, dispatch) };
  unityBridge.initBridge(value);
  unityBridge.updateState(state);
  return (
    //@ts-ignore
    <AppContext.Provider value={value}>
      <QueryClientProvider client={queryClient}>
        <ApiSessionContext.Provider value={firebaseUid}>
          <ApiLoadingProvider>{props.children}</ApiLoadingProvider>
        </ApiSessionContext.Provider>
      </QueryClientProvider>
    </AppContext.Provider>
  );
};

ActionMap.map(() => {
  console.log();
});

const ContextConsumer = AppContext.Consumer;
const UnityBridge = unityBridge;
const Events = {
  notifications: { ...currentState.notifications.events },
  preferences: { ...currentState.preferences.events },
  device: { ...currentState.device.events },
  audio: { ...currentState.audio.events },
  app: { ...currentState.app.events },
  analytics: { ...currentState.analytics.events },
  editor: { ...currentState.editor.events },
  auth: { ...currentState.auth.events },
  user: { ...currentState.user.events },
  loading: { ...currentState.loading.events },
  upload: { ...currentState.upload.events },
  admin: {
    users: { ...currentState.admin.users.events },
    teams: { ...currentState.admin.teams.events },
    subscriptions: { ...currentState.admin.subscriptions.events },
    clubs: { ...currentState.admin.clubs.events },
  },
  account: { ...currentState.account.events },
};

export {
  ActionMap,
  Actions,
  amplitude as Amplitude,
  AppContext,
  ContextConsumer,
  ContextProvider,
  currentState as CurrentState,
  Events,
  firebaseApp,
  googleProvider,
  oAuthProvider,
  UnityBridge,
};
